import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Phone, Video, MoreVertical, Check, CheckCheck, LayoutDashboard, PhoneCall, MessageSquare, Users, CalendarCheck, Zap, TrendingUp, Bot } from "lucide-react";

export const Tilt3D = ({ children, className = "", intensity = 10 }) => {
    const ref = useRef(null);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rx = useSpring(useTransform(my, [0, 1], [intensity, -intensity]), { stiffness: 140, damping: 18 });
    const ry = useSpring(useTransform(mx, [0, 1], [-intensity, intensity]), { stiffness: 140, damping: 18 });

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
    };

    const reset = () => {
        mx.set(0.5);
        my.set(0.5);
    };

    return (
        <div className={`scene-3d ${className}`} ref={ref} onMouseMove={onMove} onMouseLeave={reset}>
            <motion.div className="preserve-3d" style={{ rotateX: rx, rotateY: ry }}>
                {children}
            </motion.div>
        </div>
    );
};

const CHAT = [
    { from: "user", text: "Hi! I want to know about your services." },
    { from: "ai", text: "Hello! Welcome to WorkAssign AI. I can help with services, pricing or booking a demo. What are you looking for?" },
    { from: "user", text: "I want a demo for my business." },
    { from: "ai", text: "Great choice! May I know your business type and preferred time? I will book your demo slot right away." },
];

export const PhoneMockup = ({ className = "" }) => (
    <div className={`device-shadow w-[270px] rounded-[2.4rem] border border-white/15 bg-[#0B1220] p-2.5 ${className}`} data-testid="phone-mockup">
        <div className="overflow-hidden rounded-[1.9rem] bg-[#0A0F1C]">
            <div className="mx-auto mt-2 h-5 w-24 rounded-full bg-black/80" />
            <div className="flex items-center justify-between border-b border-white/5 bg-[#101A2E] px-4 py-3">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-400">
                        <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-white notranslate">WorkAssign AI</p>
                        <p className="text-[10px] text-emerald-400">online · replies instantly</p>
                    </div>
                </div>
                <div className="flex gap-3 text-slate-400">
                    <Video className="h-3.5 w-3.5" />
                    <Phone className="h-3.5 w-3.5" />
                    <MoreVertical className="h-3.5 w-3.5" />
                </div>
            </div>
            <div className="space-y-2.5 px-3 py-4" style={{ minHeight: 300 }}>
                {CHAT.map((m, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 12, scale: 0.95 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 + i * 0.55, duration: 0.4 }}
                        className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
                    >
                        <div
                            className={`max-w-[85%] rounded-2xl px-3 py-2 text-[11px] leading-relaxed ${
                                m.from === "user"
                                    ? "rounded-br-sm bg-emerald-600/80 text-emerald-50"
                                    : "rounded-bl-sm bg-[#1B2942] text-slate-200"
                            }`}
                        >
                            {m.text}
                            <span className="mt-0.5 flex justify-end text-[8px] text-slate-400">
                                {m.from === "user" ? <CheckCheck className="h-3 w-3 text-cyan-300" /> : <Check className="h-3 w-3" />}
                            </span>
                        </div>
                    </motion.div>
                ))}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + CHAT.length * 0.55 }}
                    className="flex justify-start"
                >
                    <div className="flex gap-1 rounded-2xl rounded-bl-sm bg-[#1B2942] px-3 py-2.5">
                        <span className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
                        <span className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
                        <span className="typing-dot h-1.5 w-1.5 rounded-full bg-slate-400" />
                    </div>
                </motion.div>
            </div>
            <div className="flex items-center gap-2 border-t border-white/5 px-3 py-2.5">
                <div className="h-8 flex-1 rounded-full bg-[#1B2942] px-3 text-[10px] leading-8 text-slate-500">Message…</div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-500">
                    <Zap className="h-3.5 w-3.5 text-white" />
                </div>
            </div>
        </div>
    </div>
);

const STATS = [
    { label: "Live Conversations", value: "148", icon: MessageSquare, color: "text-cyan-400" },
    { label: "AI Calls Today", value: "326", icon: PhoneCall, color: "text-emerald-400" },
    { label: "Qualified Leads", value: "57", icon: Users, color: "text-amber-400" },
    { label: "Appointments", value: "23", icon: CalendarCheck, color: "text-violet-400" },
];

const BARS = [35, 55, 42, 70, 62, 88, 78];

export const LaptopMockup = ({ className = "" }) => (
    <div className={`device-shadow w-[520px] max-w-full rounded-2xl border border-white/15 bg-[#0B1220] p-2 ${className}`} data-testid="laptop-mockup">
        <div className="overflow-hidden rounded-xl bg-[#0A0F1C]">
            <div className="flex items-center gap-1.5 border-b border-white/5 bg-[#101A2E] px-3 py-2">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                <span className="ml-3 rounded-md bg-[#0A0F1C] px-3 py-1 font-mono2 text-[9px] text-slate-500 notranslate">app.workassign.in/command-center</span>
            </div>
            <div className="flex">
                <div className="hidden w-28 flex-col gap-1 border-r border-white/5 bg-[#0D1424] p-2.5 sm:flex">
                    {[
                        { icon: LayoutDashboard, label: "Dashboard", active: true },
                        { icon: PhoneCall, label: "AI Calls" },
                        { icon: MessageSquare, label: "WhatsApp" },
                        { icon: Users, label: "Leads" },
                        { icon: CalendarCheck, label: "Follow-ups" },
                        { icon: Zap, label: "Automation" },
                    ].map((i) => (
                        <div
                            key={i.label}
                            className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[9px] ${
                                i.active ? "bg-cyan-500/15 text-cyan-300" : "text-slate-500"
                            }`}
                        >
                            <i.icon className="h-3 w-3" /> {i.label}
                        </div>
                    ))}
                </div>
                <div className="flex-1 p-3">
                    <div className="mb-2.5 flex items-center justify-between">
                        <p className="font-display text-[11px] font-bold text-white">AI Command Center</p>
                        <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[8px] font-semibold text-emerald-400">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> LIVE
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {STATS.map((s, i) => (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 + i * 0.15 }}
                                className="rounded-lg border border-white/5 bg-[#111B30] p-2.5"
                            >
                                <div className="flex items-center justify-between">
                                    <s.icon className={`h-3.5 w-3.5 ${s.color}`} />
                                    <TrendingUp className="h-2.5 w-2.5 text-slate-600" />
                                </div>
                                <p className="mt-1.5 font-display text-base font-extrabold text-white">{s.value}</p>
                                <p className="text-[8px] text-slate-500">{s.label}</p>
                            </motion.div>
                        ))}
                    </div>
                    <div className="mt-2 rounded-lg border border-white/5 bg-[#111B30] p-2.5">
                        <p className="mb-2 text-[8px] font-semibold uppercase tracking-wider text-slate-500">Conversations & Calls — This Week</p>
                        <div className="flex h-14 items-end gap-1.5">
                            {BARS.map((h, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ height: 0 }}
                                    whileInView={{ height: `${h}%` }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.6 + i * 0.08, duration: 0.5 }}
                                    className="flex-1 rounded-sm bg-gradient-to-t from-blue-600/60 to-cyan-400/80"
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);
